import { formatPublicPersonId } from "./publicPersonId";
import type {
  ChatRoom,
  ChatWorkflowStatus,
  ConversationKind,
} from "@/types/chat";
import { UserType } from "@/types/chat";

export type ChatMailboxView = "official" | "direct";
export type OfficialInboxFilter =
  | "all"
  | "unread"
  | "unhandled"
  | "assigned"
  | "in_progress"
  | "waiting_parent"
  | "resolved";
export type DirectInboxFilter =
  "all" | "unread" | "coach" | "individual" | "group";
export type ChatMailboxFilter = OfficialInboxFilter | DirectInboxFilter;

export const WORKFLOW_STATUS_META: Record<
  ChatWorkflowStatus,
  {
    label: string;
    shortLabel: string;
    tone: "danger" | "info" | "warning" | "success";
  }
> = {
  UNHANDLED: { label: "未担当", shortLabel: "未担当", tone: "danger" },
  IN_PROGRESS: { label: "対応中", shortLabel: "対応中", tone: "info" },
  WAITING_PARENT: {
    label: "会員の返信待ち",
    shortLabel: "返信待ち",
    tone: "warning",
  },
  RESOLVED: { label: "対応済み", shortLabel: "対応済み", tone: "success" },
};

export const getConversationKind = (room: ChatRoom): ConversationKind =>
  room.conversationKind ?? "LEGACY";

export const isOfficialInboxRoom = (room: ChatRoom): boolean =>
  getConversationKind(room) === "OFFICIAL_INBOX";

export const getNonOfficialRoomLabel = (room: ChatRoom): string => {
  if (getConversationKind(room) === "COACH_DIRECT") return "コーチ個別";
  return room.type === "GROUP" ? "グループ" : "個別";
};

export const getSubjectStudentContext = (room: ChatRoom): string | null =>
  getConversationKind(room) === "COACH_DIRECT" && room.subjectStudent?.name
    ? `${room.subjectStudent.name}さんについて`
    : null;

export const getOfficialParentName = (room: ChatRoom): string =>
  room.officialParent?.name?.trim() ||
  room.otherParticipants?.find(
    (participant) => participant.userType === UserType.PARENT
  )?.name ||
  room.displayName?.trim() ||
  "会員名未設定";

export const getWorkflowStatus = (room: ChatRoom): ChatWorkflowStatus | null =>
  isOfficialInboxRoom(room) ? (room.workflowStatus ?? "UNHANDLED") : null;

export const getHeaderChatNotificationPreview = (room: ChatRoom): string => {
  const lastMessage = room.lastMessage;
  if (!lastMessage) return "";

  if (isOfficialInboxRoom(room) && lastMessage.senderType === UserType.STAFF) {
    const staffName =
      lastMessage.actorName?.trim() ||
      room.assignedStaff?.name?.trim() ||
      "スタッフ";
    return `${staffName}が担当しました`;
  }

  return lastMessage.content;
};

export const filterOfficialRooms = (
  rooms: ChatRoom[],
  filter: OfficialInboxFilter,
  currentStaffId?: string | null
): ChatRoom[] =>
  rooms.filter((room) => {
    if (!isOfficialInboxRoom(room)) return false;
    if (filter === "unhandled") {
      return getWorkflowStatus(room) === "UNHANDLED";
    }
    if (filter === "assigned") {
      return Boolean(currentStaffId && room.assignedStaffId === currentStaffId);
    }
    if (filter === "in_progress") {
      return getWorkflowStatus(room) === "IN_PROGRESS";
    }
    if (filter === "waiting_parent") {
      return getWorkflowStatus(room) === "WAITING_PARENT";
    }
    if (filter === "resolved") {
      return getWorkflowStatus(room) === "RESOLVED";
    }
    if (filter === "unread") return room.unreadCount > 0;
    return true;
  });

export const filterDirectRooms = (
  rooms: ChatRoom[],
  filter: DirectInboxFilter
): ChatRoom[] =>
  rooms.filter((room) => {
    if (isOfficialInboxRoom(room)) return false;
    if (filter === "unread") return room.unreadCount > 0;
    if (filter === "coach") {
      return getConversationKind(room) === "COACH_DIRECT";
    }
    if (filter === "individual") {
      return (
        getConversationKind(room) !== "COACH_DIRECT" && room.type === "DIRECT"
      );
    }
    if (filter === "group") return room.type === "GROUP";
    return true;
  });

export const getMailboxRooms = (
  rooms: ChatRoom[],
  view: ChatMailboxView,
  filter: OfficialInboxFilter,
  currentStaffId?: string | null,
  directFilter: DirectInboxFilter = "all"
): ChatRoom[] =>
  view === "official"
    ? filterOfficialRooms(rooms, filter, currentStaffId)
    : filterDirectRooms(rooms, directFilter);

const getMessageTimestamp = (room: ChatRoom): number =>
  room.lastMessage ? new Date(room.lastMessage.createdAt).getTime() : 0;

export const sortChatRoomsByLatestMessage = (rooms: ChatRoom[]): ChatRoom[] =>
  [...rooms].sort((a, b) => {
    const messageDifference = getMessageTimestamp(b) - getMessageTimestamp(a);
    if (messageDifference !== 0) return messageDifference;

    const updatedDifference =
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    if (updatedDifference !== 0) return updatedDifference;
    return a.id.localeCompare(b.id);
  });

export type ChatMemberFilter = {
  memberQuery?: string;
  parentQuery?: string;
  studentQuery?: string;
  courseId?: string | "all";
};

const includesNormalized = (
  values: Array<string | number | null | undefined>,
  query: string
): boolean => {
  const normalizedQuery = query.trim().toLocaleLowerCase("ja-JP");
  if (!normalizedQuery) return true;
  return values.some((value) =>
    String(value ?? "")
      .toLocaleLowerCase("ja-JP")
      .includes(normalizedQuery)
  );
};

export const filterChatRoomsByMember = (
  rooms: ChatRoom[],
  {
    memberQuery = "",
    parentQuery = "",
    studentQuery = "",
    courseId = "all",
  }: ChatMemberFilter
): ChatRoom[] =>
  rooms.filter((room) => {
    const parentParticipants = room.otherParticipants?.filter(
      (participant) => participant.userType === UserType.PARENT
    );
    const parentMatches = includesNormalized(
      [
        room.officialParent?.name,
        room.officialParent?.nameKana,
        room.officialParent?.email,
        ...(parentParticipants?.flatMap((participant) => [
          participant.name,
          participant.email,
        ]) ?? []),
      ],
      parentQuery
    );
    const studentMatches =
      !studentQuery.trim() ||
      (room.memberStudents ?? []).some((student) =>
        includesNormalized(
          [
            student.memberNumber,
            formatPublicPersonId("S", student.memberNumber),
            student.name,
            student.nameKana,
          ],
          studentQuery
        )
      );
    const memberMatches =
      !memberQuery.trim() ||
      includesNormalized(
        [
          room.officialParent?.name,
          room.officialParent?.nameKana,
          room.officialParent?.email,
          ...(parentParticipants?.flatMap((participant) => [
            participant.name,
            participant.email,
          ]) ?? []),
        ],
        memberQuery
      ) ||
      (room.memberStudents ?? []).some((student) =>
        includesNormalized(
          [
            student.memberNumber,
            formatPublicPersonId("S", student.memberNumber),
            student.name,
            student.nameKana,
          ],
          memberQuery
        )
      );
    const courseMatches =
      !courseId ||
      courseId === "all" ||
      (room.memberStudents ?? []).some((student) =>
        student.courses.some((course) => course.id === courseId)
      );

    return memberMatches && parentMatches && studentMatches && courseMatches;
  });

export const mergeChatRoomSummary = (
  current: ChatRoom,
  updated: ChatRoom
): ChatRoom => ({
  ...current,
  ...updated,
  userChatRooms: updated.userChatRooms ?? current.userChatRooms,
  currentUser: updated.currentUser ?? current.currentUser,
  otherParticipants: updated.otherParticipants ?? current.otherParticipants,
  facilityIdentity: updated.facilityIdentity ?? current.facilityIdentity,
  officialParent: updated.officialParent ?? current.officialParent,
  subjectStudent: updated.subjectStudent ?? current.subjectStudent,
  memberStudents: updated.memberStudents ?? current.memberStudents,
});
